'use client';

import { useState, useEffect } from 'react';
import { APP_URL } from '@/lib/config';

export interface AuthCheckResult {
  isAuthenticated: boolean;
  isChecking: boolean;
  dashboardUrl: string;
  signInUrl: string;
  signUpUrl: string;
}

export function useAuthCheck(): AuthCheckResult {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isChecking, setIsChecking] = useState<boolean>(true);

  useEffect(() => {
    try {
      // 1. Check cookies for active session indicators
      const cookies = typeof document !== 'undefined' ? document.cookie : '';
      
      const hasSessionCookie = Boolean(
        cookies.includes('__session=') ||
        cookies.includes('__clerk_db_jwt=') ||
        cookies.includes('postbrix_session=') ||
        cookies.includes('pb_session=') ||
        cookies.includes('pb_user=') ||
        cookies.includes('auth_token=') ||
        cookies.includes('session=')
      );

      // Check Clerk active user timestamp cookie (__client_uat > 0)
      const uatMatch = cookies.match(/__client_uat=([^;]+)/);
      const hasActiveUat = uatMatch && uatMatch[1] ? parseInt(uatMatch[1], 10) > 0 : false;

      // 2. Check localStorage & sessionStorage for persisted tokens/user profiles
      let hasStorageSession = false;
      try {
        const localUser =
          localStorage.getItem('pb_user') ||
          localStorage.getItem('postbrix_user') ||
          localStorage.getItem('clerk-db-jwt') ||
          localStorage.getItem('auth_token') ||
          localStorage.getItem('user');

        const sessionUser =
          sessionStorage.getItem('pb_user') ||
          sessionStorage.getItem('postbrix_user');

        if ((localUser && localUser !== 'null' && localUser !== 'undefined') ||
            (sessionUser && sessionUser !== 'null' && sessionUser !== 'undefined')) {
          hasStorageSession = true;
        }
      } catch {
        // Storage access might fail in third-party iframe contexts
      }

      const isAuth = hasSessionCookie || hasActiveUat || hasStorageSession;
      setIsAuthenticated(isAuth);
    } catch {
      setIsAuthenticated(false);
    } finally {
      setIsChecking(false);
    }
  }, []);

  return {
    isAuthenticated,
    isChecking,
    dashboardUrl: `${APP_URL}/dashboard`,
    signInUrl: `${APP_URL}/sign-in`,
    signUpUrl: `${APP_URL}/sign-up`,
  };
}
